<template>
    <div>
        <page-header>Analytics Goals</page-header>

        <vx-alert v-if="success" class="mb-6" color="success" :closeable="false" shadow>
            Updated successfully
        </vx-alert>

        <vx-alert v-if="!loading && !metrics" color="danger" :closeable="false" shadow>
            <div class="flex items-center justify-between">
                Failed to load data
                <vx-button @click="fetchData" size="sm" color="danger"
                    >Retry<font-awesome-icon widthAuto :icon="faRefresh"
                /></vx-button>
            </div>
        </vx-alert>

        <page-loading v-if="loading"></page-loading>

        <vx-box v-else-if="!loading && metrics" color="info">
            <div class="flex flex-col gap-6">
                <div class="pt-8 px-8 flex flex-col gap-1">
                    <span class="text-base font-extrabold">Category Metrics</span>
                    <span class="font-sans text-sm text-slate-500 max-w-xl">
                        Set the goal level of each metric for each message category. Blast campaigns are automatically
                        categorized based on their message content.
                    </span>
                </div>

                <div>
                    <!-- header -->
                    <div class="lg:grid hidden bg-white py-4 px-8 grid-cols-5 text-xs text-black">
                        <span>Category</span>
                        <vx-popover hover arrow placement="top" class="max-w-fit">
                            <span>{{ metricAbbreviation('unique_click_through_rate') }}</span>
                            <template v-slot:content>{{ metricLabel('unique_click_through_rate') }}</template>
                        </vx-popover>
                        <vx-popover hover arrow placement="top" class="max-w-fit">
                            <span>{{ metricAbbreviation('click_through_rate') }}</span>
                            <template v-slot:content>{{ metricLabel('click_through_rate') }}</template>
                        </vx-popover>
                        <vx-popover hover arrow placement="top" class="max-w-fit">
                            <span>{{ metricAbbreviation('opt_out_rate') }}</span>
                            <template v-slot:content>{{ metricLabel('opt_out_rate') }}</template>
                        </vx-popover>
                        <vx-popover hover arrow placement="top" class="max-w-fit">
                            <span>{{ metricAbbreviation('message_reply_rate') }}</span>
                            <template v-slot:content>{{ metricLabel('message_reply_rate') }}</template>
                        </vx-popover>
                    </div>

                    <!-- rows -->
                    <div
                        v-for="(data, category) in metrics"
                        :key="category"
                        :data-test="`category-row-${category}`"
                        class="border-t border-solid border-x-0 border-b-0 border-slate-200 py-4 px-8 grid lg:grid-cols-5 sm:grid-cols-3 grid-cols-1 even:bg-slate-50 odd:bg-slate-100"
                    >
                        <div class="flex gap-2 items-center sm:pr-4 pb-6 sm:pb-0">
                            <font-awesome-icon fixedWidth class="text-slate-400" :icon="getCategoryIcon(category)" />
                            <span class="text-slate-700 text-sm font-extrabold">{{ getCategoryLabel(category) }}</span>
                        </div>

                        <div class="col-span-2 lg:col-span-4 grid grid-cols-1 lg:grid-cols-4 gap-1">
                            <analytics-goals-target-input
                                v-for="metric in metricsWithGoals"
                                :key="`${category}_${metric}`"
                                :category="category"
                                :metric="metric"
                                v-model="metrics"
                            ></analytics-goals-target-input>
                        </div>
                    </div>
                </div>

                <div class="w-full px-8 pb-8">
                    <vx-button
                        @click.prevent="save()"
                        :disabled="!metrics"
                        type="button"
                        color="primary"
                        size="lg"
                        class="w-full"
                        data-test="save"
                        :loading="saving"
                    >
                        Update
                    </vx-button>
                </div>
            </div>
        </vx-box>
    </div>
</template>

<script setup>
import PageHeader from '~/components/general/PageHeader.vue';
import { VxAlert, VxBox, VxButton, VxPopover } from '@voxie/frontend-components';
import { ref } from 'vue';
import { outboundMessagesAnalyticsClient } from '~/services';
import { CATEGORIES } from '~/constants/categories';
import AnalyticsGoalsTargetInput from '~/components/settings/teams/analytics-goals/AnalyticsGoalsTargetInput.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PageLoading from '~/components/general/PageLoading.vue';
import { faRefresh, faSquareDashed } from '@fortawesome/pro-solid-svg-icons';
import useVuelidate from '@vuelidate/core';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { metricAbbreviation, metricLabel, metricsWithGoals } from '~/utils/analytics';
import { validateRules } from '~/utils/validation';

const teamId = Spark.state.currentTeam.id;

const { catchError } = useFormRequestHandler();

const loading = ref(false);
const saving = ref(false);
const success = ref(false);
const metrics = ref();

const validation = useVuelidate();

const getCategoryIcon = (category) => CATEGORIES.find((cat) => cat.value === category)?.icon || faSquareDashed;
const getCategoryLabel = (category) => CATEGORIES.find((cat) => cat.value === category)?.label || category;

const fetchData = async () => {
    loading.value = true;

    try {
        const response = await outboundMessagesAnalyticsClient.getCategoryPerformanceTargets({ teamId });

        const targets = response?.data?.targets;
        if (!targets || typeof targets !== 'object') {
            return {};
        }

        metrics.value = Object.keys(targets)
            .sort()
            .reduce((acc, key) => {
                acc[key] = { ...targets[key] };
                return acc;
            }, {});
    } catch (e) {
        catchError(e);
    } finally {
        loading.value = false;
    }
};

const save = async () => {
    if (saving.value || loading.value) {
        return;
    }

    success.value = false;
    const isValid = await validateRules(validation);

    if (!isValid) {
        return;
    }

    saving.value = true;

    try {
        await outboundMessagesAnalyticsClient.setCategoryPerformanceTargets({ teamId }, { targets: metrics.value });
        success.value = true;
        window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
        catchError(e);
    } finally {
        saving.value = false;
    }
};

fetchData();
</script>
